import { Icon, Button, Form, RadioLabel, TranslateDropdown, Input, Modal, CurrencyInputLabel, Counter, CheckboxLabel } from '@weezevent/nacre';
import React from 'react';
import Style from '../../../style/editCrossSelling.module.css';
import Styles from '../../../../../styles/rate/groupModal.module.css';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../../../components/loadingCard';
import useAppContext from '../../../../../context/useAppContext';
import { getProductsLanguages, getDefaultLanguage, getLanguagesTranslateDropdown, removeDefaultLanguage } from '../../../utils/translationUtils';
import i18n from 'i18next';

export default function VariantModal({ open, setOpenModal, product, quotas, form, setForm, setFormObject, formErrors, resetModal, handleSave, loading }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const locale = i18n.language;

    const [unlimitedQuantity, setUnlimitedQuantity] = React.useState(form.id ? (quotas ? !quotas.find(q => q.variant === form.id)?.quota_total : !form?.quota) : true);
    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);
    const languagesTranslateDropdown = React.useMemo(() => getLanguagesTranslateDropdown(productsLanguages), [productsLanguages]);

    const onSave = React.useCallback(
        (newVariant = false) => {
            const basePayload = {
                reference: form.reference,
                disabled: form.disabled,
                price: form.price,
                name: form.name,
                quota: form.quota,
                ordering: form.ordering,
                translations: removeDefaultLanguage(form.translations, defaultLanguage)
            };

            if (form.id) {
                // Edit : basePayload + id
                handleSave({ ...basePayload, id: form.id }, { newVariant, id: form.id });
            } else {
                // Save : basePayload + transaction count at 0
                handleSave({ ...basePayload, transaction_total_count: 0 }, { newVariant });
            }
        },
        [form, handleSave, defaultLanguage]
    );

    return (
        <Modal
            className={Styles['group-modal']}
            size={'large'}
            open={open}
            allowExternalClosing={false}
            onClose={() => {
                setOpenModal(false);
                resetModal();
            }}
            title={form.id ? t('ticket.cross-selling.modalVariant.titleUpdate') : t('ticket.cross-selling.modalVariant.title')}
            subtitle={form.id ? t('ticket.cross-selling.modalVariant.subtitleUpdate') : t('ticket.cross-selling.modalVariant.subtitle')}
            submitButton={
                <>
                    {!form.id && (
                        <Button
                            primary
                            inverted
                            onClick={() => {
                                onSave(true);
                            }}
                            label={t('ticket.cross-selling.modalVariant.addNewVariant')}
                        />
                    )}
                    <Button
                        primary
                        onClick={() => {
                            onSave();
                        }}
                        label={!form.id ? t('ticket.common.action.add') : t('ticket.common.action.update')}
                    />
                </>
            }
            cancelButton={
                <Button
                    onClick={() => {
                        setOpenModal(false);
                        resetModal();
                    }}
                    label={t('ticket.common.action.cancel')}
                />
            }
        >
            <LoadingCard loading={loading}>
                <Form.Field>
                    <VariantNameInput
                        formState={form}
                        setFormObject={setFormObject}
                        defaultLanguage={defaultLanguage}
                        languageOptions={languagesTranslateDropdown}
                        errorName={formErrors['name'] ? true : false}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.ref')} optionalText={t('ticket.common.labelOptional')} />
                    <Input value={form.reference} onChange={event => setFormObject({ ...form, reference: event.target.value })} />
                </Form.Field>

                <Form.Field>
                    <CurrencyInputLabel
                        key="commission"
                        style={{ width: '137px' }}
                        label={t('ticket.discountForm.grid.tarif_ttc')}
                        currency={organization.currency}
                        locale={locale}
                        numberDecimals={2}
                        value={form.price}
                        onChange={({ value }) => setFormObject({ ...form, price: value })}
                        errors={formErrors['price'] ? true : false}
                    />
                </Form.Field>

                <span className={Style['add-cross-modal-variant-costs-text']}>{`${t('ticket.cross-selling.modalVariant.frais')} ${
                    product?.category === 1 ? '1.25' : '2.5'
                } %`}</span>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.quotas')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Style['add-cross-modal-variant-quota-container']}>
                        <Counter
                            disabled={unlimitedQuantity}
                            numberDecimals={0}
                            min={1}
                            onChange={(e, value) => setForm('quota', value)}
                            step={1}
                            value={form.quota || 0}
                            hideArrows={true}
                        />
                        <CheckboxLabel
                            className={Style['add-cross-modal-variant-quota-checkbox']}
                            name="checkboxQuota"
                            label={t('ticket.cross-selling.modalVariant.illimitedQuantity')}
                            checked={unlimitedQuantity}
                            onChange={() => {
                                setUnlimitedQuantity(!unlimitedQuantity);
                                setFormObject({ ...form, quota: unlimitedQuantity ? 0 : null });
                            }}
                        />
                    </div>
                </Form.Field>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.visibilityOnlineTitle')} />
                    <RadioLabel
                        checked={!form.disabled}
                        onClick={() => {
                            setFormObject({ ...form, disabled: false });
                        }}
                        name="visible-variant"
                        value="variant-visibility-1"
                        inline
                        bordered
                    >
                        {t('ticket.cross-selling.modalVariant.visibilityOnline')} {<Icon name="eye" />}
                    </RadioLabel>
                    <RadioLabel
                        checked={form.disabled}
                        onClick={() => {
                            setFormObject({ ...form, disabled: true });
                        }}
                        name="visible-variant"
                        value="variant-visibility-2"
                        inline
                        bordered
                    >
                        {t('ticket.common.action.hide-online')} {<Icon name="eye-slash" />}
                    </RadioLabel>
                </Form.Field>
            </LoadingCard>
        </Modal>
    );
}

function VariantNameInput({ formState, setFormObject, defaultLanguage, languageOptions, errorName }) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const handleTranslations = React.useCallback(
        value => {
            let ret = { ...formState };
            ret.translations.name[selectedNameLanguage] = value || '';
            if (selectedNameLanguage === defaultLanguage) ret.name = value;
            setFormObject(ret);
        },
        [setFormObject, formState, defaultLanguage, selectedNameLanguage]
    );

    return (
        <>
            <Form.Label className={Styles['remove-margin-bottom']}>
                <div className={Styles['group-label-input']}>
                    {t('ticket.cross-selling.modalVariant.nameItem')}
                    <TranslateDropdown
                        defaultValue={defaultLanguage}
                        languages={languageOptions}
                        onChange={e => setSelectedNameLanguage(e)}
                        selected={Object.entries(formState.translations.name)
                            .map(([k, v]) => (v ? k : undefined))
                            .filter(e => e)}
                    />
                </div>
            </Form.Label>
            <Form.Label className={Styles['group-modal-form-label-subtitle']}>{t(`ticket.rates.groupModal.groupNameSubtitle`)}</Form.Label>
            <Form.Input value={formState.translations.name[selectedNameLanguage] || ''} onChange={({ target: { value } }) => handleTranslations(value)} errors={errorName} />
        </>
    );
}
